/**
 * ContactAssignmentRulesDialog
 *
 * Manage tag- and custom-field-based assignment rules: each rule auto-assigns
 * UNASSIGNED contacts whose tags or custom-field conditions match to a specific
 * user or role. Manual assignments always win.
 * Saves the full rules array via PUT (whole-array replace) and supports
 * "Apply rules now" which runs the rules immediately on existing contacts.
 */
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Add, ArrowDownward, ArrowUpward, Delete as DeleteIcon } from '@mui/icons-material';
import adminApi, { AssignmentRule, TeamMember } from '@/services/admin.api';
import userManagementService, { Role } from '@/services/user-management.service';

/** Monotonic counter so newly-added (unsaved) rules have a stable key for React. */
let _idCounter = 0;
function nextLocalId(): string {
  return `_local_${++_idCounter}`;
}

const MAX_FIELD_PAIRS = 10;

interface Props {
  open: boolean;
  onClose: () => void;
  /** All known tags for this tenant (populates the tag Autocomplete options). */
  allTags: string[];
}

const ContactAssignmentRulesDialog: React.FC<Props> = ({ open, onClose, allTags }) => {
  const [rules, setRules] = useState<AssignmentRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState('');
  const [applyResult, setApplyResult] = useState<{ scanned: number; assigned: number } | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  /** Edits live only in local state until "Save rules" — without this the dialog
   *  looks identical whether a removed rule was persisted or merely un-rendered,
   *  which reads as "I deleted it" while the rule is still routing contacts. */
  const [dirty, setDirty] = useState(false);
  const [attributeKeys, setAttributeKeys] = useState<string[]>([]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError('');
    setApplyResult(null);
    setDirty(false);

    Promise.all([
      adminApi.getContactAssignmentRules(),
      adminApi.getTeamDirectory({ suppressPermissionLock: true }),
      userManagementService.getRoles().catch(() => [] as Role[]),
      adminApi.getContactAttributeKeys({ suppressPermissionLock: true }).catch(() => [] as string[]),
    ])
      .then(([fetchedRules, members, fetchedRoles, fetchedKeys]) => {
        setRules(fetchedRules || []);
        setTeamMembers(members || []);
        setRoles(fetchedRoles || []);
        setAttributeKeys(fetchedKeys || []);
      })
      .catch((e: any) => {
        setError(e?.response?.data?.error?.message || e.message || 'Failed to load rules');
      })
      .finally(() => setLoading(false));
  }, [open]);

  const addRule = () => {
    setRules((prev) => [
      ...prev,
      { id: nextLocalId(), tags: [], customFields: [], assignTo: { kind: 'user', userId: '' } },
    ]);
    setDirty(true);
  };

  const removeRule = (idx: number) => {
    setRules((prev) => prev.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const moveRule = (idx: number, direction: 'up' | 'down') => {
    const swapWith = direction === 'up' ? idx - 1 : idx + 1;
    if (swapWith < 0 || swapWith >= rules.length) return;
    setRules((prev) => {
      const next = [...prev];
      const tmp = next[idx]!;
      next[idx] = next[swapWith]!;
      next[swapWith] = tmp;
      return next;
    });
    setDirty(true);
  };

  const setRuleField = (idx: number, patch: Partial<AssignmentRule>) => {
    setRules((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
    setDirty(true);
  };

  const addFieldPair = (ruleIdx: number) => {
    setRules((prev) =>
      prev.map((r, i) => {
        if (i !== ruleIdx) return r;
        const pairs = r.customFields ?? [];
        if (pairs.length >= MAX_FIELD_PAIRS) return r;
        return { ...r, customFields: [...pairs, { key: '', value: '' }] };
      })
    );
    setDirty(true);
  };

  const updateFieldPair = (
    ruleIdx: number,
    pairIdx: number,
    field: 'key' | 'value',
    val: string
  ) => {
    setRules((prev) =>
      prev.map((r, i) => {
        if (i !== ruleIdx) return r;
        const pairs = (r.customFields ?? []).map((p, pi) =>
          pi === pairIdx ? { ...p, [field]: val } : p
        );
        return { ...r, customFields: pairs };
      })
    );
    setDirty(true);
  };

  const removeFieldPair = (ruleIdx: number, pairIdx: number) => {
    setRules((prev) =>
      prev.map((r, i) => {
        if (i !== ruleIdx) return r;
        return { ...r, customFields: (r.customFields ?? []).filter((_, pi) => pi !== pairIdx) };
      })
    );
    setDirty(true);
  };

  const handleSave = async () => {
    // Validate: each rule must have at least one tag or one non-empty field pair
    for (let i = 0; i < rules.length; i++) {
      const rule = rules[i]!;
      const nonEmptyPairs = (rule.customFields ?? []).filter(
        (p) => p.key.trim() !== '' && p.value.trim() !== ''
      );
      if (rule.tags.length === 0 && nonEmptyPairs.length === 0) {
        setError(
          `Rule ${i + 1} has no conditions. Add at least one tag or one custom-field pair.`
        );
        return;
      }
    }

    // Strip empty pairs before saving
    const cleaned = rules.map((r) => ({
      ...r,
      customFields: (r.customFields ?? []).filter(
        (p) => p.key.trim() !== '' && p.value.trim() !== ''
      ),
    }));

    setSaving(true);
    setError('');
    try {
      const saved = await adminApi.saveContactAssignmentRules(cleaned);
      setRules(saved || cleaned);
      setDirty(false);
    } catch (e: any) {
      const reason = e?.response?.data?.error?.message || e.message || 'Failed to save rules';
      // Say plainly that the server is unchanged — the list on screen still shows
      // the attempted edit, so a bare error reads as "saved, with a warning".
      setError(`${reason} — nothing was saved; the rules currently in effect are unchanged.`);
    } finally {
      setSaving(false);
    }
  };

  const handleApply = async () => {
    setApplying(true);
    setError('');
    setApplyResult(null);
    try {
      const result = await adminApi.applyContactAssignmentRules();
      setApplyResult({ scanned: result.scanned, assigned: result.assigned });
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e.message || 'Failed to apply rules');
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Assignment Rules</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          Rules automatically assign <strong>unassigned</strong> contacts when ANY of their tags
          or custom-field conditions match. Manual assignments always take precedence.
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Rules are checked top to bottom — the first match wins. Use the arrows to set priority.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {dirty && !loading && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Unsaved changes. Rules keep routing contacts exactly as before until you click{' '}
            <strong>Save rules</strong> — removing a rule here does not stop it on its own.
          </Alert>
        )}

        {applyResult && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Applied: {applyResult.scanned} contacts scanned,{' '}
            {applyResult.assigned} newly assigned.
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={26} />
          </Box>
        ) : (
          <Stack spacing={2.5}>
            {rules.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No rules yet. Add one below.
              </Typography>
            )}

            {rules.map((rule, idx) => {
              const fieldPairs = rule.customFields ?? [];
              return (
                <Box
                  key={rule.id}
                  sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                >
                  <Stack spacing={1.5}>
                    {/* Tags condition */}
                    <Autocomplete
                      multiple
                      freeSolo
                      size="small"
                      options={allTags}
                      value={rule.tags}
                      onChange={(_, value) => {
                        setRuleField(idx, {
                          tags: value.map((v) => String(v).trim()).filter(Boolean),
                        });
                      }}
                      renderTags={(value, getTagProps) =>
                        value.map((tag, index) => (
                          <Chip
                            label={tag}
                            size="small"
                            {...getTagProps({ index })}
                            key={tag}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...(params as any)}
                          label="Tags (optional) — match any"
                          placeholder={rule.tags.length ? '' : 'Type a tag and press Enter…'}
                        />
                      )}
                    />

                    {/* Custom field conditions */}
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                        Custom field conditions (optional) — match any
                      </Typography>
                      <Stack spacing={0.75}>
                        {fieldPairs.map((pair, pairIdx) => (
                          <Stack key={pairIdx} direction="row" spacing={0.75} alignItems="center">
                            <Autocomplete
                              freeSolo
                              size="small"
                              options={attributeKeys}
                              value={pair.key}
                              onInputChange={(_, val) =>
                                updateFieldPair(idx, pairIdx, 'key', val)
                              }
                              onChange={(_, val) =>
                                updateFieldPair(idx, pairIdx, 'key', String(val ?? ''))
                              }
                              sx={{ flex: 1 }}
                              renderInput={(params) => (
                                <TextField
                                  {...(params as any)}
                                  label="Key"
                                  inputProps={{
                                    ...(params as any).inputProps,
                                    maxLength: 60,
                                  }}
                                />
                              )}
                            />
                            <TextField
                              size="small"
                              label="Value"
                              value={pair.value}
                              onChange={(e) =>
                                updateFieldPair(idx, pairIdx, 'value', e.target.value)
                              }
                              inputProps={{ maxLength: 200 }}
                              sx={{ flex: 1 }}
                            />
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => removeFieldPair(idx, pairIdx)}
                              aria-label="Remove field condition"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        ))}
                      </Stack>
                      <Button
                        size="small"
                        startIcon={<Add />}
                        onClick={() => addFieldPair(idx)}
                        disabled={fieldPairs.length >= MAX_FIELD_PAIRS}
                        sx={{ mt: 0.5 }}
                      >
                        Add field condition
                      </Button>
                    </Box>

                    {/* Assign-to: kind selector + value */}
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <FormControl size="small" sx={{ minWidth: 110, flexShrink: 0 }}>
                        <InputLabel id={`rule-kind-${idx}`}>Assign by</InputLabel>
                        <Select
                          labelId={`rule-kind-${idx}`}
                          label="Assign by"
                          value={rule.assignTo.kind}
                          onChange={(e) => {
                            const kind = e.target.value as 'user' | 'role';
                            setRuleField(idx, {
                              assignTo:
                                kind === 'user'
                                  ? { kind: 'user', userId: '' }
                                  : { kind: 'role', roleIds: [] },
                            });
                          }}
                        >
                          <MenuItem value="user">User</MenuItem>
                          <MenuItem value="role">Role</MenuItem>
                        </Select>
                      </FormControl>

                      {rule.assignTo.kind === 'user' ? (
                        <FormControl size="small" fullWidth>
                          <InputLabel id={`rule-user-${idx}`}>Team member</InputLabel>
                          <Select
                            labelId={`rule-user-${idx}`}
                            label="Team member"
                            value={rule.assignTo.userId}
                            onChange={(e) => {
                              setRuleField(idx, {
                                assignTo: { kind: 'user', userId: e.target.value },
                              });
                            }}
                          >
                            {teamMembers.map((m) => (
                              <MenuItem key={m._id} value={m._id}>
                                {`${m.firstName} ${m.lastName}`.trim() || m.email}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <Autocomplete
                          multiple
                          size="small"
                          fullWidth
                          options={roles}
                          getOptionLabel={(r) => r.name}
                          value={roles.filter(
                            (r) =>
                              rule.assignTo.kind === 'role' &&
                              rule.assignTo.roleIds.includes(r._id)
                          )}
                          onChange={(_, value) => {
                            setRuleField(idx, {
                              assignTo: { kind: 'role', roleIds: value.map((v) => v._id) },
                            });
                          }}
                          renderTags={(value, getTagProps) =>
                            value.map((r, index) => (
                              <Chip
                                label={r.name}
                                size="small"
                                {...getTagProps({ index })}
                                key={r._id}
                              />
                            ))
                          }
                          renderInput={(params) => (
                            <TextField
                              {...(params as any)}
                              label="Roles"
                              placeholder={
                                rule.assignTo.kind === 'role' && rule.assignTo.roleIds.length
                                  ? ''
                                  : 'Select roles…'
                              }
                            />
                          )}
                        />
                      )}

                      <Stack direction="column" sx={{ flexShrink: 0 }}>
                        <IconButton
                          size="small"
                          onClick={() => moveRule(idx, 'up')}
                          disabled={idx === 0}
                          aria-label="Move rule up"
                        >
                          <ArrowUpward fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => moveRule(idx, 'down')}
                          disabled={idx === rules.length - 1}
                          aria-label="Move rule down"
                        >
                          <ArrowDownward fontSize="small" />
                        </IconButton>
                      </Stack>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeRule(idx)}
                        sx={{ mt: 0.5, flexShrink: 0 }}
                        aria-label="Remove rule"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Box>
              );
            })}

            <Button
              size="small"
              startIcon={<Add />}
              onClick={addRule}
              sx={{ alignSelf: 'flex-start' }}
            >
              Add rule
            </Button>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
        <Button
          variant="outlined"
          size="small"
          // Apply runs the rules the SERVER has stored, not what's on screen — so
          // running it with unsaved edits would silently apply the old rules.
          disabled={applying || saving || loading || dirty}
          onClick={handleApply}
          startIcon={applying ? <CircularProgress size={14} /> : undefined}
          {...(dirty ? { title: 'Save your changes first' } : {})}
        >
          Apply rules now
        </Button>
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose} disabled={saving}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || loading}
            startIcon={saving ? <CircularProgress size={14} /> : undefined}
          >
            Save rules
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default ContactAssignmentRulesDialog;
